import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  GoogleMap,
  Marker,
  OverlayView,
  OverlayViewF,
  Polygon,
  Polyline,
} from '@react-google-maps/api';
import { useSelector } from 'react-redux';
import { useGoogleMaps } from '../../components/GoogleMapsProvider';
import type { RootState } from '../../redux-toolkit/store';
import { CustomParagraph } from '../../components';
import AirportIcon from '../../assets/images/airport.png';
import { Tooltip } from 'antd';
import { truncateByLength } from '../../common/helper';
import { getMapStyles } from '@/common/GoogleMapStyle.js';
import type { LatLng, SingleShipmentDataExtended } from '@/apis/types';
import { CoCEventType } from '@/common/enum';
import { getShipmentLocationParsedAddress } from '@/common/utils/utils';
import {
  downsampleEvenly,
  getIndexedPrimarySensorPoints,
  getSensorPointId,
  hasValidSensorCoordinates,
  isSensorPointId,
  MAX_SENSOR_MARKERS,
  MAX_SENSOR_POLYLINE_POINTS,
  simplifyPolyline,
  SensorTrackShipment,
} from './mapSensorUtils';
import SensorCanvasLayer from './SensorCanvasLayer';
import { shareHidesSensorPoints } from './partials/sensorVitals';

interface NullableLatLng {
  lat: number | null;
  lng: number | null;
}

const ignoreSensorClick = (): void => undefined;

/**
 * Pairs each item with a key from its identity plus how many times that
 * identity has already appeared, so repeated items (a route can repeat a
 * vertex verbatim) still get unique, position-free React keys.
 */
const withOccurrenceKeys = <T,>(
  items: readonly T[],
  identity: (item: T) => string
): Array<{ item: T; key: string }> => {
  const seen = new Map<string, number>();
  return items.map((item) => {
    const id = identity(item);
    const occurrence = seen.get(id) ?? 0;
    seen.set(id, occurrence + 1);
    return { item, key: `${id}#${occurrence}` };
  });
};

/** A route vertex's identity: two vertices can share a timestamp, or repeat exactly. */
const vertexIdentity = (point: {
  latitude?: number | null;
  longitude?: number | null;
  lat_updated?: string | null;
}): string => `${point.latitude},${point.longitude},${point.lat_updated ?? ''}`;

const isValidCoordinate = (val: number | null | undefined): val is number =>
  typeof val === 'number' && val !== 0;

/** The coordinates as a map position, or null when either is missing or zero. */
const toLatLng = (
  coordinates: Partial<NullableLatLng> | null | undefined
): LatLng | null =>
  coordinates &&
  isValidCoordinate(coordinates.lat) &&
  isValidCoordinate(coordinates.lng)
    ? { lat: coordinates.lat, lng: coordinates.lng }
    : null;

export interface TrackingPoint {
  latitude: number;
  longitude: number;
  lat?: number;
  lng?: number;
  lat_updated?: string;
  shipment_id?: string;
  shipment_sensor_id?: string | null;
  action?: string;
  location?: string;
  timestamp?: string;
  handler?: string;
  sensor_tracking_id?: string | null;
  sensor_priority?: number | null;
  temperature?: string | number;
  temperature_updated?: string | null;
  temperature_age_seconds?: number | null;
  humidity?: string | number;
  battery?: string | number;
  battery_updated?: string | null;
  battery_age_seconds?: number | null;
  tilt?: string | number;
  pressure?: string | number;
  light?: string | number;
  shock_value?: string | number;
  cell_signal?: string | number;
  /** Stable index in the original shipment_monitor array. */
  _sensorIndex?: number;
}

// Extended point type that includes an index for identification
export interface SensorPointWithIndex extends TrackingPoint {
  _sensorIndex: number;
}

type TrackingPointWithLatLng = TrackingPoint & LatLng;

interface ShipmentPolylineSegment {
  polyline: TrackingPoint[];
  mode: string;
  status: string;
}

export interface CourierEventPoint {
  id: string;
  latitude: number;
  longitude: number;
  type: number;
  action?: string;
  location?: string;
  timestamp?: string;
  handler?: string;
}

export const isCourierEventPoint = (
  point: TrackingPoint | CourierEventPoint | SensorPointWithIndex
): point is CourierEventPoint => {
  return 'type' in point && point.type === CoCEventType.CustodyEvent;
};

export const isSensorPointWithIndex = (
  point: TrackingPoint | CourierEventPoint | SensorPointWithIndex
): point is SensorPointWithIndex => {
  return '_sensorIndex' in point && typeof point._sensorIndex === 'number';
};

interface ShipmentMapProps {
  onPointClicked: (
    point: TrackingPoint | CourierEventPoint | SensorPointWithIndex,
    index?: number
  ) => void;
  height?: string;
  shipment: Partial<SingleShipmentDataExtended>;
  lastCoordinates?: NullableLatLng | null;
  isLargeScreen?: boolean;
  currentView?: 'summary' | 'detail';
  selectedPointId?: string | null;
  /** Coordinates to recenter on, preserving the current zoom; null leaves the map alone. */
  focusPoint?: { lat: number; lng: number } | null;
  minZoom?: number;
  /** Mobile opens centered on the latest location instead of fitting all points. */
  isMobile?: boolean;
}

/**
 * ShipmentMap - Displays an interactive Google Map showing shipment tracking information
 *
 * @param onPointClicked - Callback when a tracking point marker is clicked
 * @param height - Optional height for the map container (default: '320px')
 * @param shipment - Shipment data including locations and tracking points
 * @param lastCoordinates - Current position of the shipment (truck icon)
 * @param isLargeScreen - Whether to show full map controls and polygons (default: false)
 * @param currentView - Map view mode: 'summary' shows courier routes, 'detail' shows tracking points
 */
const ShipmentMap: React.FC<ShipmentMapProps> = ({
  onPointClicked,
  height,
  shipment,
  isLargeScreen,
  currentView,
  selectedPointId,
  focusPoint,
  minZoom,
  isMobile = false,
}) => {
  const { isLoaded } = useGoogleMaps();
  const darkModeEnabled = useSelector(
    (state: RootState) => state.appearance.darkModeEnabled
  );
  const mapStyles = useMemo(
    () => getMapStyles(darkModeEnabled),
    [darkModeEnabled]
  );
  const [points, setPoints] = useState<TrackingPoint[]>([]);
  const [fromCoordinates, setFromCoordinates] = useState<NullableLatLng>({
    lat: null,
    lng: null,
  });
  const [toCoordinates, setToCoordinates] = useState<NullableLatLng>({
    lat: null,
    lng: null,
  });
  const [fromPlaceName, setFromPlaceName] = useState<string>('');
  const [toPlaceName, setToPlaceName] = useState<string>('');
  const [_path, setPath] = useState<NullableLatLng[]>([]);
  const [sourcePopupPosition, setSourcePopupPosition] = useState<LatLng | null>(
    null
  );
  const [destinationPopupPosition, setDestinationPopupPosition] =
    useState<LatLng | null>(null);

  const [sourcePopupContent, setSourcePopupContent] =
    useState<React.ReactNode>(null);
  const [destinationPopupContent, setDestinationPopupContent] =
    useState<React.ReactNode>(null);

  const [fromPolygon, setFromPolygon] = useState<LatLng[] | null>(null);
  const [toPolygon, setToPolygon] = useState<LatLng[] | null>(null);
  const fromLatLng = useMemo(
    () => toLatLng(fromCoordinates),
    [fromCoordinates]
  );
  const toLatLngPosition = useMemo(
    () => toLatLng(toCoordinates),
    [toCoordinates]
  );

  // Courier events from chain_of_custody (type=2) with coordinates
  const [courierEvents, setCourierEvents] = useState<CourierEventPoint[]>([]);

  const [currentZoom, setCurrentZoom] = useState<number>(5);

  // Check if any sensor points have valid location data (lat/lng)
  // Used to decide whether to show sensor layer or fall back to courier locations
  const sensorHasLocationData = Array.isArray(points) && points.length > 0;

  // Zoom-adaptive point budgets: fewer points when zoomed out (dots overlap anyway),
  // full budget at high zoom for maximum detail.
  const adaptiveMaxPolyline = useMemo(() => {
    if (currentZoom <= 4) return 150;
    if (currentZoom <= 6) return 400;
    if (currentZoom <= 9) return 700;
    return MAX_SENSOR_POLYLINE_POINTS;
  }, [currentZoom]);

  const adaptiveMaxMarkers = useMemo(() => {
    if (currentZoom <= 4) return 80;
    if (currentZoom <= 6) return 200;
    if (currentZoom <= 9) return 350;
    return MAX_SENSOR_MARKERS;
  }, [currentZoom]);

  const sensorPolylinePoints = useMemo(
    () => simplifyPolyline(points, adaptiveMaxPolyline),
    [points, adaptiveMaxPolyline]
  );
  const selectedSensorSourceIndex = useMemo(() => {
    if (!isSensorPointId(selectedPointId)) return null;
    return (
      points.find((point) => getSensorPointId(point) === selectedPointId)
        ?._sensorIndex ?? null
    );
  }, [selectedPointId, points]);
  const sensorMarkerPoints = useMemo(() => {
    const sampled = downsampleEvenly(points, adaptiveMaxMarkers);
    if (selectedSensorSourceIndex == null) return sampled;
    if (
      sampled.some((point) => point._sensorIndex === selectedSensorSourceIndex)
    ) {
      return sampled;
    }
    const selectedPoint = points.find(
      (point) => point._sensorIndex === selectedSensorSourceIndex
    );
    return selectedPoint
      ? [...sampled, selectedPoint].sort(
          (a, b) => (a._sensorIndex ?? 0) - (b._sensorIndex ?? 0)
        )
      : sampled;
  }, [points, adaptiveMaxMarkers, selectedSensorSourceIndex]);
  const sensorPointsHidden = shareHidesSensorPoints(shipment);
  // Sensor data off: only the latest position is drawn, so the route still
  // shows where the shipment is without exposing each reading point.
  const visibleSensorMarkers = useMemo(
    () => (sensorPointsHidden ? points.slice(-1) : sensorMarkerPoints),
    [sensorPointsHidden, points, sensorMarkerPoints]
  );
  const selectedSensorMarkerIndex = useMemo(
    () =>
      selectedSensorSourceIndex == null
        ? null
        : sensorMarkerPoints.findIndex(
            (point) => point._sensorIndex === selectedSensorSourceIndex
          ),
    [selectedSensorSourceIndex, sensorMarkerPoints]
  );

  const normalizeShipmentStatus = (value?: string | null) =>
    (value || '')
      .toLowerCase()
      .trim()
      .replace(/[\s-]+/g, '_');

  // A shipment is only truly "awaiting pickup" if there is no real tracking data
  // yet. If any CoC event or monitor_info reading has valid coordinates, the
  // shipment has started and we should show the sensor/courier layers.
  const isAwaitingPickup = useMemo(() => {
    const monitorInfo = shipment?.monitor_info;
    const monitorInfoPoints = Array.isArray(monitorInfo)
      ? monitorInfo
      : monitorInfo
        ? [monitorInfo]
        : [];
    const hasProgress =
      (Array.isArray(shipment?.chain_of_custody)
        ? shipment.chain_of_custody
        : []
      ).some(
        (c) => c.type === CoCEventType.CustodyEvent && c.latitude && c.longitude
      ) ||
      monitorInfoPoints.some(
        (m) =>
          typeof m.latitude === 'number' &&
          m.latitude !== 0 &&
          typeof m.longitude === 'number' &&
          m.longitude !== 0
      );
    if (hasProgress) return false;
    return (
      normalizeShipmentStatus(shipment?.custom_status?.current_stage) ===
        'awaiting_pickup' ||
      normalizeShipmentStatus(shipment?.status) === 'awaiting_pickup'
    );
  }, [shipment]);

  const isDelivered =
    normalizeShipmentStatus(shipment?.status) === 'delivered' ||
    normalizeShipmentStatus(shipment?.custom_status?.current_stage) ===
      'delivered';

  const mapRef = useRef<google.maps.Map | null>(null);
  const zoomTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setFromPlaceName('');
    setToPlaceName('');
    setPoints([]);
    if (shipment) {
      setFromCoordinates(
        shipment?.from_location?.latlng
          ? shipment?.from_location?.latlng
          : { lat: null, lng: null }
      );
      setToCoordinates(
        shipment?.to_location?.latlng
          ? shipment?.to_location?.latlng
          : { lat: null, lng: null }
      );
      const fromName = shipment?.from_location?.name;
      const toName = shipment?.to_location?.name;

      setFromPlaceName(
        typeof fromName === 'string' &&
          fromName.toLowerCase() === 'unknown location'
          ? getShipmentLocationParsedAddress(shipment?.from_location)
          : fromName || ''
      );

      setToPlaceName(
        typeof toName === 'string' &&
          toName.toLowerCase() === 'unknown location'
          ? getShipmentLocationParsedAddress(shipment?.to_location)
          : toName || ''
      );

      setFromPolygon(shipment?.from_location?.polygon ?? null);
      setToPolygon(shipment?.to_location?.polygon ?? null);

      // Keep map markers and point-card IDs aligned for both full history and
      // legacy/minimal responses that only provide monitor_info. The detail
      // payload's monitor points are TrackingPoints whose vitals may be null.
      setPoints(
        getIndexedPrimarySensorPoints(
          shipment as SensorTrackShipment<TrackingPoint>
        )
      );

      // Extract courier events (type=2) with valid coordinates from chain_of_custody
      if (shipment?.chain_of_custody && shipment?.chain_of_custody.length > 0) {
        const eventsWithCoords = shipment.chain_of_custody
          .filter(
            (item) =>
              item.type === CoCEventType.CustodyEvent &&
              typeof item.latitude === 'number' &&
              typeof item.longitude === 'number' &&
              item.latitude !== 0 &&
              item.longitude !== 0
          )
          .map((item) => ({
            id: item.id,
            latitude: item.latitude as number,
            longitude: item.longitude as number,
            type: item.type,
            action: item.action,
            location: item.location,
            timestamp: item.timestamp,
            handler: item.handler,
          }))
          .sort(
            (a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
        setCourierEvents(eventsWithCoords);
      } else {
        setCourierEvents([]);
      }
    }
  }, [shipment]);

  useEffect(() => {
    setPath([]);
    if (points.length > 0 && toCoordinates?.lat && toCoordinates?.lng) {
      const last = {
        lat: points[points.length - 1]?.latitude,
        lng: points[points.length - 1]?.longitude,
      };
      const arr: NullableLatLng[] = [];
      if (last.lat && last.lng) {
        arr.push({ lat: last?.lat, lng: last?.lng });
      }
      if (toCoordinates?.lat && toCoordinates?.lng) {
        arr.push({ lat: toCoordinates?.lat, lng: toCoordinates?.lng });
      }
      if (arr.length > 0) {
        setPath(arr);
      }
    }
  }, [toCoordinates, points]);

  useEffect(() => {
    setSourcePopupContent(null);
    setSourcePopupPosition(null);
    if (fromLatLng) {
      setSourcePopupPosition(fromLatLng);
      setSourcePopupContent(
        <Tooltip title={fromPlaceName}>
          <span>
            <CustomParagraph
              ellipsis={true}
              text={truncateByLength(fromPlaceName, 20)}
              fontSize='14px'
              fontWeight={600}
              marginBottom={0}
            />
          </span>
        </Tooltip>
      );
    }
  }, [fromLatLng, fromPlaceName]);

  useEffect(() => {
    setDestinationPopupContent(null);
    setDestinationPopupPosition(null);
    if (toLatLngPosition) {
      setDestinationPopupPosition(toLatLngPosition);
      setDestinationPopupContent(
        <Tooltip title={toPlaceName}>
          <span>
            <CustomParagraph
              ellipsis={true}
              text={truncateByLength(toPlaceName, 20)}
              fontSize='14px'
              fontWeight={600}
              marginBottom={0}
            />
          </span>
        </Tooltip>
      );
    }
  }, [toLatLngPosition, toPlaceName]);

  // Marker/line colors tuned for the light map tiles need brighter values
  // once the dark map style is on, or they blend into the tile instead of
  // popping against it -- Google Maps symbol/icon colors are plain hex, not
  // CSS custom properties, so this can't just be a var() swap like the rest
  // of the app.
  // "Secondary" (origin pin, courier dots, flight track): was a flat dark
  // gray -- on the dark map that read as almost the same value as the tile
  // itself, so dark mode gets a near-white instead of a muted mid-gray.
  const secondaryMarkerColor = useMemo(
    () => (darkModeEnabled ? '#F2F3F5' : '#4B4B4B'),
    [darkModeEnabled]
  );
  // Accent (route line, tracking dots, halo): green in dark mode so the
  // route reads clearly against the dark tiles; unchanged blue in light
  // mode.
  const accentColor = useMemo(
    () => (darkModeEnabled ? '#34D399' : '#266EF1'),
    [darkModeEnabled]
  );
  // Origin/destination pins: unchanged from the original light-mode design
  // (dark-gray fill / white fill) -- only dark mode swaps to solid green/
  // orange, since that pale gray-on-gray design is what needed the fix.
  const originMarkerColor = useMemo(
    () => (darkModeEnabled ? '#22C55E' : '#4B4B4B'),
    [darkModeEnabled]
  );
  const destinationMarkerColor = useMemo(
    () => (darkModeEnabled ? '#F97316' : '#FFFFFF'),
    [darkModeEnabled]
  );

  // Memoized with empty deps so Google Maps never sees a new object reference,
  // which would cause every Marker/Polyline to unmount and remount each render.
  const polylineOptions = useMemo(
    () => ({
      strokeColor: accentColor,
      strokeOpacity: 1.0,
      strokeWeight: 3,
      fillColor: accentColor,
      icons: [
        {
          icon: {
            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 2,
            strokeColor: accentColor,
            fillColor: accentColor,
          },
          offset: '50%',
          repeat: '100px',
        },
      ],
    }),
    [accentColor]
  );

  const polylinePath = useMemo(
    () => ({
      strokeColor: secondaryMarkerColor,
      strokeOpacity: 1,
      strokeWeight: 3,
      fillColor: secondaryMarkerColor,
      icons: null as google.maps.IconSequence[] | null,
    }),
    [secondaryMarkerColor]
  );

  // const markerSize = useMemo(
  //   () => ({
  //     url: Truck,
  //     scaledSize: new window.google.maps.Size(40, 40),
  //   }),
  //   []
  // );

  const mapContainerStyle = useMemo(
    () => ({ width: '100%', height: height || '320px' }),
    [height]
  );

  const circleIcon = useMemo(
    () => ({
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: accentColor,
      fillOpacity: 1,
      strokeColor: accentColor,
      strokeWeight: 2,
      scale: 4,
    }),
    [accentColor]
  );

  const selectedCircleIcon = useMemo(
    () => ({
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: accentColor,
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 3,
      scale: 8,
    }),
    [accentColor]
  );

  const courierEventIcon = useMemo(
    () => ({
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: secondaryMarkerColor,
      fillOpacity: 1,
      strokeColor: secondaryMarkerColor,
      strokeWeight: 2,
      scale: 4,
    }),
    [secondaryMarkerColor]
  );

  const selectedCourierEventIcon = useMemo(
    () => ({
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: secondaryMarkerColor,
      fillOpacity: 1,
      strokeColor: '#FFFFFF',
      strokeWeight: 3,
      scale: 8,
    }),
    [secondaryMarkerColor]
  );

  const latestUpdateHaloIcon = useMemo(
    () => ({
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: accentColor,
      fillOpacity: 0.2,
      strokeColor: accentColor,
      strokeWeight: 1,
      scale: 12,
    }),
    [accentColor]
  );

  // Origin: unchanged dark-gray dot in light mode; a solid green dot with a
  // white ring in dark mode so it reads as a pin against the dark map style.
  const blackMarker = useMemo(
    () => ({
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: originMarkerColor,
      fillOpacity: 1,
      strokeColor: darkModeEnabled ? '#FFFFFF' : originMarkerColor,
      strokeWeight: 2,
      scale: 10,
    }),
    [originMarkerColor, darkModeEnabled]
  );

  // Destination: unchanged white dot with a gray ring in light mode; a solid
  // orange dot with a white ring in dark mode, mirroring the origin marker.
  const whiteMarker = useMemo(
    () => ({
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: destinationMarkerColor,
      fillOpacity: 1,
      strokeColor: darkModeEnabled ? '#FFFFFF' : '#4B4B4B',
      strokeWeight: darkModeEnabled ? 2 : 3,
      scale: 10,
    }),
    [destinationMarkerColor, darkModeEnabled]
  );

  const getCurrentPoint = (
    item: TrackingPoint | CourierEventPoint | SensorPointWithIndex,
    index?: number
  ) => {
    onPointClicked(item, index);
  };

  const handleSensorClick = useCallback(
    (index: number, point: TrackingPoint) => {
      const pointWithIndex: SensorPointWithIndex = {
        ...point,
        _sensorIndex: point._sensorIndex ?? index,
      };
      onPointClicked(pointWithIndex, index);
    },
    [onPointClicked]
  );

  const handleZoomChanged = useCallback(() => {
    if (zoomTimerRef.current) clearTimeout(zoomTimerRef.current);
    zoomTimerRef.current = setTimeout(() => {
      if (mapRef.current) {
        setCurrentZoom(mapRef.current.getZoom() ?? 5);
      }
    }, 120);
  }, []);

  const previousBoundsRef = useRef<google.maps.LatLngBounds | null>(null);

  useEffect(() => {
    if (isLoaded) {
      const bounds = new window.google.maps.LatLngBounds();
      // Extend bounds to include the origin and destination
      if (fromLatLng) bounds.extend(fromLatLng);
      if (toLatLngPosition) bounds.extend(toLatLngPosition);

      points.forEach((point) => {
        bounds.extend({ lat: point.latitude, lng: point.longitude });
      });
      // Include courier events in bounds calculation
      courierEvents.forEach((event) => {
        if (
          isValidCoordinate(event.latitude) &&
          isValidCoordinate(event.longitude)
        ) {
          bounds.extend({ lat: event.latitude, lng: event.longitude });
        }
      });

      const boundsAreEqual = (
        b1: google.maps.LatLngBounds | null,
        b2: google.maps.LatLngBounds
      ) => {
        if (!b1) return false;
        const b1NE = b1.getNorthEast();
        const b1SW = b1.getSouthWest();
        const b2NE = b2.getNorthEast();
        const b2SW = b2.getSouthWest();
        if (!b1NE || !b1SW || !b2NE || !b2SW) return false;
        return (
          b1NE.lat() === b2NE.lat() &&
          b1NE.lng() === b2NE.lng() &&
          b1SW.lat() === b2SW.lat() &&
          b1SW.lng() === b2SW.lng()
        );
      };
      // Fit the map to the calculated bounds
      if (
        mapRef?.current &&
        !bounds.isEmpty() &&
        !boundsAreEqual(previousBoundsRef.current, bounds)
      ) {
        // Mobile: last known location, or destination once delivered.
        const lastPoint = points[points.length - 1];
        const lastEvent = courierEvents[courierEvents.length - 1];
        const mobileCenter = [
          isDelivered ? toLatLngPosition : null,
          lastPoint
            ? toLatLng({ lat: lastPoint.latitude, lng: lastPoint.longitude })
            : null,
          lastEvent
            ? toLatLng({ lat: lastEvent.latitude, lng: lastEvent.longitude })
            : null,
          toLatLngPosition,
        ].find((center): center is LatLng => center !== null);

        if (isMobile && mobileCenter) {
          mapRef.current.setCenter(mobileCenter);
          mapRef.current.setZoom(3);
        } else {
          mapRef.current.fitBounds(bounds);
        }
        previousBoundsRef.current = bounds;
      }
    }
  }, [
    isLoaded,
    isMobile,
    isDelivered,
    fromLatLng,
    toLatLngPosition,
    points,
    courierEvents,
  ]);

  // Recenter on an externally selected point (Previous/Next in the point card).
  // panTo moves the camera only, so the user's zoom level is preserved.
  useEffect(() => {
    if (!focusPoint || !mapRef.current) return;
    const { lat, lng } = focusPoint;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    mapRef.current.panTo({ lat, lng });
  }, [focusPoint]);

  //All code relative to summary view
  const [currentMapView, setCurrentMapView] = useState<string>('summary');
  const [courierTrack, setCourierTrack] = useState<ShipmentPolylineSegment[]>(
    []
  );

  useEffect(() => {
    setCurrentMapView(currentView ? currentView : 'summary');
  }, [currentView]);

  useEffect(() => {
    if (currentView === 'summary') {
      if (
        shipment &&
        shipment?.shipment_polyline &&
        shipment?.shipment_polyline?.length > 0
      ) {
        setCourierTrack(
          shipment?.shipment_polyline.filter(
            (item) =>
              item?.polyline &&
              item?.polyline?.length > 0 &&
              item.status === 'travelled'
          )
        );
      }
    } else {
      setCourierTrack([]);
    }
  }, [shipment, currentMapView, currentView]);

  const airMarker = useMemo(
    () => ({
      url: AirportIcon,
      scaledSize: new window.google.maps.Size(30, 30),
    }),
    []
  );

  return (
    <>
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          options={{
            disableDefaultUI: !isLargeScreen, // Disables map controls if not large screen
            zoomControl: isLargeScreen, // Only show zoom control on large screens
            streetViewControl: isLargeScreen, // Only show street view control on large screens
            mapTypeControl: isLargeScreen, // Only show map type control on large screens
            minZoom,
            gestureHandling: isMobile ? 'greedy' : 'auto',
            styles: mapStyles,
          }}
          onLoad={(map) => {
            mapRef.current = map;
          }}
          onZoomChanged={handleZoomChanged}
        >
          {fromLatLng && (
            <>
              {isLargeScreen ? (
                <>
                  {fromPolygon ? (
                    <Polygon
                      paths={fromPolygon}
                      options={{
                        fillColor: '#FF0000',
                        fillOpacity: 0.35,
                        strokeColor: '#FF0000',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                      }}
                    />
                  ) : null}

                  <Marker position={fromLatLng} icon={blackMarker} />
                </>
              ) : (
                <Marker position={fromLatLng} icon={blackMarker} />
              )}
            </>
          )}

          {toLatLngPosition && (
            <>
              {isLargeScreen ? (
                <>
                  {toPolygon ? (
                    <Polygon
                      paths={toPolygon}
                      options={{
                        fillColor: '#FF0000',
                        fillOpacity: 0.35,
                        strokeColor: '#FF0000',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                      }}
                    />
                  ) : null}

                  <Marker position={toLatLngPosition} icon={whiteMarker} />
                </>
              ) : (
                <Marker position={toLatLngPosition} icon={whiteMarker} />
              )}
            </>
          )}

          {/* OverlayViewF positions in draw() itself; class OverlayView lags a frame and flickers on pan. */}
          {sourcePopupPosition && (
            <OverlayViewF
              position={sourcePopupPosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div className='custom-popup-info-window popup-accent-origin'>
                {sourcePopupContent}
              </div>
            </OverlayViewF>
          )}

          {destinationPopupPosition && (
            <OverlayViewF
              position={destinationPopupPosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div className='custom-popup-info-window destination-popup-window popup-accent-destination'>
                {destinationPopupContent}
              </div>
            </OverlayViewF>
          )}

          {currentMapView === 'detail' ? (
            <>
              {/* {lastCoordinates &&
                lastCoordinates?.lat &&
                lastCoordinates?.lng && (
                  <Marker position={lastCoordinates} icon={markerSize} />
                )} */}

              {!isAwaitingPickup && sensorHasLocationData && (
                <>
                  {/* Sensor points polyline (blue, connected). Suppressed
                      when flight_track is present — that route is
                      authoritative for this shipment and would otherwise
                      overlap/compete with the sensor's own (typically
                      sparser) line near the endpoints. */}
                  {!shipment?.flight_track &&
                    sensorPolylinePoints.length > 1 && (
                      <Polyline
                        path={sensorPolylinePoints.map((point) => ({
                          lat: point.latitude,
                          lng: point.longitude,
                        }))}
                        options={polylineOptions}
                      />
                    )}

                  {/* Canvas overlay — renders all sensor dots at 60fps without per-marker React overhead */}
                  <SensorCanvasLayer
                    points={visibleSensorMarkers}
                    selectedIndex={
                      !sensorPointsHidden &&
                      selectedSensorMarkerIndex !== null &&
                      selectedSensorMarkerIndex >= 0
                        ? selectedSensorMarkerIndex
                        : null
                    }
                    onPointClick={
                      sensorPointsHidden ? ignoreSensorClick : handleSensorClick
                    }
                    pulseLatest={!isDelivered}
                  />
                </>
              )}

              {/* Live flight track (FlightAware) — only present on shipments
                  whose courier/status/resolution match the backend gate in
                  ShipmentServices.get_shipment_details(). Independent of the
                  sensor/CoC tracking above; renders nothing for any other
                  shipment. */}
              {!isAwaitingPickup && shipment?.flight_track && (
                <>
                  {shipment.flight_track.flown_path &&
                    shipment.flight_track.flown_path.length > 1 && (
                      <Polyline
                        path={shipment.flight_track.flown_path.map((point) => ({
                          lat: point.latitude,
                          lng: point.longitude,
                        }))}
                        options={polylinePath}
                      />
                    )}
                  {shipment.flight_track.current_position && (
                    <Marker
                      position={{
                        lat: shipment.flight_track.current_position.latitude,
                        lng: shipment.flight_track.current_position.longitude,
                      }}
                      icon={{
                        path: 'M21,16v-2l-8-5V3.5c0-0.83-0.67-1.5-1.5-1.5S10,2.67,10,3.5V9l-8,5v2l8-2.5V19l-2,1.5V22l3.5-1l3.5,1v-1.5L13,19v-4.5L21,16z',
                        scale: 1.4,
                        anchor: new window.google.maps.Point(12, 12),
                        rotation:
                          shipment.flight_track.current_position.heading ?? 0,
                        fillColor: secondaryMarkerColor,
                        fillOpacity: 1,
                        strokeColor: secondaryMarkerColor,
                        strokeWeight: 1,
                      }}
                      zIndex={100}
                    />
                  )}
                </>
              )}

              {/* Courier events - black dots when sensor exists, blue connected when courier-only */}
              {!isAwaitingPickup && courierEvents?.length > 0 && (
                <>
                  {/* Courier-only: show polyline connecting courier events when no sensor or sensor has no location data */}
                  {!sensorHasLocationData && courierEvents?.length > 1 && (
                    <Polyline
                      path={courierEvents.map((event) => ({
                        lat: event.latitude,
                        lng: event.longitude,
                      }))}
                      options={polylineOptions}
                    />
                  )}

                  {courierEvents.map((event, index) => {
                    const isSelected = selectedPointId === event.id;
                    const isLatestCourier =
                      !sensorHasLocationData &&
                      index === courierEvents?.length - 1;

                    // Determine icon based on whether sensor has location data
                    // Sensor with location data: courier = black dots (secondary layer)
                    // No sensor or sensor without location data: courier = blue dots with polyline (primary layer)
                    const normalIcon = sensorHasLocationData
                      ? courierEventIcon
                      : circleIcon;
                    const selectedIcon = sensorHasLocationData
                      ? selectedCourierEventIcon
                      : selectedCircleIcon;

                    return (
                      <React.Fragment key={event.id}>
                        {/* Latest update halo for courier-only scenario */}
                        {isLatestCourier && (
                          <Marker
                            position={{
                              lat: event.latitude,
                              lng: event.longitude,
                            }}
                            icon={latestUpdateHaloIcon}
                            clickable={false}
                            zIndex={1}
                          />
                        )}
                        <Marker
                          onClick={() => getCurrentPoint(event, index)}
                          position={{
                            lat: event.latitude,
                            lng: event.longitude,
                          }}
                          icon={isSelected ? selectedIcon : normalIcon}
                          zIndex={isSelected ? 100 : isLatestCourier ? 50 : 5}
                        />
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </>
          ) : (
            <>
              {courierTrack?.length > 0
                ? withOccurrenceKeys(
                    courierTrack,
                    (segment) =>
                      `${segment.mode}-${segment.status}-${
                        segment.polyline[0]
                          ? vertexIdentity(segment.polyline[0])
                          : ''
                      }`
                  ).map(({ item: element, key: segmentKey }) => {
                    let courierPath: TrackingPointWithLatLng[] = [];

                    if (element?.polyline && element.polyline.length > 0) {
                      courierPath = element.polyline
                        .filter((point) => hasValidSensorCoordinates(point))
                        .map((point) => ({
                          lat: point.latitude,
                          lng: point.longitude,
                          ...point,
                        }));
                    }

                    const mode = element.mode;

                    return (
                      <React.Fragment key={segmentKey}>
                        {/* Render Polyline */}
                        {!isAwaitingPickup && (
                          <Polyline
                            path={courierPath}
                            options={
                              mode === 'air' ? polylinePath : polylineOptions
                            }
                          />
                        )}

                        {/* Render Marker for each valid point */}
                        {withOccurrenceKeys<TrackingPointWithLatLng>(
                          courierPath,
                          vertexIdentity
                        ).map(({ item: point, key }, idx) => (
                          <Marker
                            key={`${segmentKey}-${key}`}
                            position={{ lat: point.lat, lng: point.lng }}
                            icon={mode === 'air' ? airMarker : circleIcon}
                            onClick={() => getCurrentPoint(point, idx)}
                          />
                        ))}
                      </React.Fragment>
                    );
                  })
                : /* Fallback: when no polyline data (e.g. sensor with no location data),
                   show CoC courier events as the tracking layer */
                  courierEvents?.length > 0 && (
                    <>
                      {!isAwaitingPickup && courierEvents.length > 1 && (
                        <Polyline
                          path={courierEvents.map((event) => ({
                            lat: event.latitude,
                            lng: event.longitude,
                          }))}
                          options={polylineOptions}
                        />
                      )}
                      {courierEvents.map((event, index) => {
                        const isLatestCourier =
                          index === courierEvents.length - 1;
                        return (
                          <React.Fragment key={event.id}>
                            {isLatestCourier && (
                              <Marker
                                position={{
                                  lat: event.latitude,
                                  lng: event.longitude,
                                }}
                                icon={latestUpdateHaloIcon}
                                clickable={false}
                                zIndex={1}
                              />
                            )}
                            <Marker
                              onClick={() => getCurrentPoint(event, index)}
                              position={{
                                lat: event.latitude,
                                lng: event.longitude,
                              }}
                              icon={circleIcon}
                              zIndex={isLatestCourier ? 50 : 10}
                            />
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
            </>
          )}
        </GoogleMap>
      ) : (
        <h1>Loading...</h1>
      )}
    </>
  );
};

export default ShipmentMap;
